import React, { useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { toast } from "sonner";
import { Navigate, useLocation } from "react-router-dom";
import Axios from "axios";
import { Data } from "../../../Data";
import { getError } from "../../../utils/utils";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Spinner } from "../../../components/ui/spinner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogBody,
    DialogCloseButton,
} from "../../../components/ui/dialog";
import LegalInfo from "../../components/LegalInfo";
import useBodyLock from "../../utils/useBodyLock";

const Tooltip = ({ text, children }) => {
    const anchorRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [style, setStyle] = useState({ position: "fixed", visibility: "hidden" });
    const [caretLeft, setCaretLeft] = useState(12);
    const TOOLTIP_WIDTH = 224;
    const GAP = 8;

    const computePosition = () => {
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const vw = window.innerWidth;

        let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
        if (left + TOOLTIP_WIDTH + GAP > vw) left = vw - TOOLTIP_WIDTH - GAP;
        if (left < GAP) left = GAP;

        const anchorCenter = rect.left + rect.width / 2;
        const caret = Math.max(12, Math.min(anchorCenter - left - 4, TOOLTIP_WIDTH - 20));

        setStyle({
            position: "fixed",
            top: rect.top - GAP,
            left,
            transform: "translateY(-100%)",
            width: TOOLTIP_WIDTH,
        });
        setCaretLeft(caret);
    };

    const show = () => { computePosition(); setVisible(true); };
    const hide = () => setVisible(false);

    return (
        <span
            ref={anchorRef}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
            className="inline-flex items-center cursor-help"
        >
            {children}
            <span
                role="tooltip"
                style={style}
                className={`pointer-events-none rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg transition-opacity z-[9999] ${visible ? "opacity-100" : "opacity-0"}`}
            >
                {text}
                <span
                    style={{ left: caretLeft }}
                    className="absolute top-full border-4 border-transparent border-t-gray-900"
                    aria-hidden="true"
                />
            </span>
        </span>
    );
};

const reducer = (state, action) => {
    switch (action.type) {
        case "FETCH_REQUEST":
            return { ...state, loading: true };
        case "FETCH_SUCCESS":
            return { ...state, authData: action.payload, loading: false };
        case "FETCH_FAIL":
            return { ...state, loading: false };
        case "FETCH_REQUEST_BANKID":
            return { ...state, loadingBankid: true };
        case "FETCH_SUCCESS_BANKID":
            return { ...state, loadingBankid: false };
        case "FETCH_FAIL_BANKID":
            return { ...state, loadingBankid: false };
        case "FETCH_REQUEST_AUTH":
            return { ...state, loadingAuth: true };
        case "FETCH_SUCCESS_AUTH":
            return { ...state, loadingAuth: false };
        case "FETCH_FAIL_AUTH":
            return { ...state, loadingAuth: false };
        case "FETCH_REQUEST_LOGIN":
            return { ...state, loadingLogin: true };
        case "FETCH_SUCCESS_LOGIN":
            return { ...state, loadingLogin: false };
        case "FETCH_FAIL_LOGIN":
            return { ...state, loadingLogin: false };
        default:
            return state;
    }
};

const BankIdLogo = () => (
    <svg
        width="190"
        height="28"
        viewBox="0 0 190 38"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-[80px] pr-[10px] mr-[10px] shrink-0"
        style={{ borderRight: "1px solid #fff" }}
    >
        <path
            d="M162.165 7.511h8.601c6.777 0 10.165 5.634 10.165 11.216 0 5.634-3.388 11.215-10.165 11.215h-8.601v-2.608h3.649V10.12h-3.649V7.51zm-4.952 29.473h13.814c12.354 0 18.506-9.128 18.506-18.257 0-9.13-6.152-18.258-18.506-18.258h-13.814v36.515zM143.139 7.511h7.819V.47h-7.819v7.042zm0 2.609h7.819v26.864h-7.819V10.12zm-32.528 26.864v-8.972h2.606l7.82 8.972h7.767v-2.608l-9.852-11.059 9.591-10.59V10.12h-7.454l-7.872 8.972v3.6h-2.606V.468h-7.819v36.515h7.819zm-21.633 0h7.819V20.71c0-6.73-4.9-11.111-10.374-11.111-2.294 0-4.691.782-6.829 2.452v3.025h-2.606V10.12H70.21v26.864h7.82V22.117c0-3.755 2.867-5.738 5.681-5.738 2.659 0 5.266 1.774 5.266 5.478v15.127zM49.465 16.275c3.492 0 6.411 2.817 6.411 7.25 0 4.435-2.919 7.252-6.411 7.252-3.493 0-6.62-2.817-6.62-7.251s3.127-7.251 6.62-7.251zm-14.7 7.25c0 8.66 6.203 13.981 12.354 13.981 2.45 0 4.9-.835 6.933-2.712v-2.765h2.606v4.955h7.298V10.12h-7.298v4.955h-2.606v-2.764c-2.033-1.879-4.483-2.713-6.933-2.713-6.151 0-12.355 5.32-12.355 13.928zM8.23 6.99h9.488c2.919 0 4.43 2.086 4.43 4.173 0 2.086-1.511 4.173-4.43 4.173h-6.881v6.781h7.558c2.92 0 4.431 2.087 4.431 4.174 0 2.086-1.512 4.173-4.43 4.173H8.23V6.99zm19.6 12.78h-4.274v-2.608h3.753c2.138-1.774 3.232-4.33 3.232-6.938 0-4.851-3.805-9.755-11.729-9.755H.151v36.515h19.392c7.976 0 11.834-5.06 11.834-10.015 0-2.765-1.2-5.425-3.545-7.199z"
            fill="#fff"
        />
    </svg>
);

export default function Main() {
    const location = useLocation();
    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );

    const [authStatus, setAuthStatus] = useState(0);
    const [showAuth, setShowAuth] = useState(false);
    const handleCloseAuth = () => {
        setShowAuth(false);
        setAuthStatus(0);
    };

    const [authToken, setAuthToken] = useState("");
    const [authCode, setAuthCode] = useState("");
    const [birthNo, setBirthNo] = useState("");
    const [refNo, setRefNo] = useState("");
    const { state, dispatch: ctxDispatch } = useContext(Data);
    const { userInfo } = state;
    const [
        { loading, loadingAuth, loadingLogin, loadingBankid, authData },
        dispatch,
    ] = useReducer(reducer, {
        authData: [],
        loading: false,
        loadingAuth: false,
        loadingLogin: false,
        loadingBankid: false,
    });

    useBodyLock(loadingBankid);

    const authEndpoint = `${process.env.REACT_APP_BANKID_ISSUER}auth`;
    const loginEndpoint = process.env.REACT_APP_REDIRECT_URI;

    const scopes = [
        "openid",
        "profile.birthdate",
        "profile.email",
        "profile.locale",
        "profile.name",
        "profile.phonenumber",
        "profile.updatedat",
        "profile.zoneinfo",
    ];

    const authUriParams = {
        client_id: "242ef172-bd71-469d-af9f-d92ae759b704",
        scope: scopes.join(" "),
        redirect_uri: loginEndpoint,
        response_type: "code",
        state: "auth_state",
        nonce: crypto.randomUUID()
            ? crypto.randomUUID()
            : Math.random().toString(36).substring(2, 15),
    };

    const uriParams = new URLSearchParams(authUriParams);
    const authUri = `${authEndpoint}?${uriParams}`;

    useEffect(() => {
        const accessCode = queryParams.get("code");
        const message = queryParams.get("message");

        if (message) {
            switch (message) {
                case "success":
                    toast.success("Úspěch");
                    break;
                case "error":
                    toast.error("Chyba");
                    break;
                case "logout":
                    toast.info("Odhlášení proběhlo úspěšně");
                    break;
                default:
                    toast.info(message);
                    break;
            }
            queryParams.delete("message");
            window.history.replaceState({}, "", location.pathname);
        }

        if (accessCode) {
            const processingKey = `bankid_${accessCode}`;
            if (sessionStorage.getItem(processingKey)) return;
            sessionStorage.setItem(processingKey, "1");

            const params = new URLSearchParams(location.search);
            params.delete("code");
            params.delete("traceId");
            params.delete("state");
            const newSearch = params.toString();
            window.history.replaceState(
                {},
                "",
                location.pathname + (newSearch ? "?" + newSearch : "")
            );

            const bankidHandler = async () => {
                dispatch({ type: "FETCH_REQUEST_BANKID" });
                try {
                    const { data } = await Axios.post(
                        "/api/auth/bankid",
                        {},
                        { headers: { code: `Bearer ${accessCode}` } }
                    );
                    dispatch({ type: "FETCH_SUCCESS_BANKID" });
                    ctxDispatch({ type: "USER_SIGNIN", payload: data });
                    localStorage.setItem("userInfo", JSON.stringify(data));
                    sessionStorage.removeItem(processingKey);
                    toast.success("Úspěšně přihlášen!");
                } catch (err) {
                    dispatch({ type: "FETCH_FAIL_BANKID" });
                    sessionStorage.removeItem(processingKey);
                    const errDesc = err?.response?.data?.error_description || "";
                    if (
                        errDesc.includes("already used") ||
                        errDesc.includes("expired")
                    ) {
                        toast.error(
                            "Přihlašovací odkaz byl již použit. Prosím přihlaste se znovu."
                        );
                    } else {
                        toast.error(getError(err));
                    }
                    console.error("[BankID Error]", err);
                }
            };
            bankidHandler();
        }
    }, [queryParams, location.search, location.pathname, ctxDispatch, dispatch]);

    const authHandler = async (e, method) => {
        e.preventDefault();
        setAuthCode("");
        dispatch({ type: "FETCH_REQUEST_AUTH" });
        try {
            const data = await Axios.post("/api/auth/auth", {
                method,
                birthNo,
                refNo,
            });
            setAuthToken(data.data);
            dispatch({ type: "FETCH_SUCCESS_AUTH" });
            toast.info("Zadejte prosím ověřovací kód.");
            setAuthStatus(1);
        } catch (err) {
            dispatch({ type: "FETCH_FAIL_AUTH" });
            toast.error(getError(err));
        }
    };

    const authLoginHandler = async (e) => {
        e.preventDefault();
        dispatch({ type: "FETCH_REQUEST_LOGIN" });
        try {
            const { data } = await Axios.post(
                "/api/auth/auth-login",
                { code: authCode, birthNo, refNo },
                { headers: { session: `Bearer ${authToken}` } }
            );
            ctxDispatch({ type: "USER_SIGNIN", payload: data });
            localStorage.setItem("userInfo", JSON.stringify(data));
            dispatch({ type: "FETCH_SUCCESS_LOGIN" });
            toast.success("Úspěšně přihlášen!");
        } catch (err) {
            dispatch({ type: "FETCH_FAIL_LOGIN" });
            toast.error(getError(err));
            setAuthCode("");
        }
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        setAuthCode("");
        dispatch({ type: "FETCH_REQUEST" });
        try {
            if (birthNo.length <= 0 && refNo.length <= 0) {
                toast.error("Zadejte prosím referenční, nebo rodné číslo!");
                dispatch({ type: "FETCH_FAIL" });
                return;
            }
            const { data } = await Axios.post("/api/auth/login", { birthNo, refNo });
            dispatch({ type: "FETCH_SUCCESS", payload: data });
            toast.info("Ověřte prosím Vaši totožnost");
            setShowAuth(true);
        } catch (err) {
            dispatch({ type: "FETCH_FAIL", payload: getError(err) });
            toast.error(getError(err));
            setRefNo("");
            setBirthNo("");
        }
    };

    if (userInfo) return <Navigate to="/" />;

    return (
        <>
            <Helmet>
                <title>Portál pro dlužníky | De Vries Group, a.s.</title>
            </Helmet>
            <main className="bg-gray-50">
                {loadingBankid && (
                    <div className="loading-box-global">
                        <Spinner size="xl" />
                        <h1 className="text-2xl font-medium">Načítání, prosím vyčkejte...</h1>
                    </div>
                )}

                <Dialog open={showAuth} onOpenChange={(open) => { if (!open) handleCloseAuth(); }}>
                    <DialogContent size="md" className="relative">
                        <DialogCloseButton />
                        <DialogHeader>
                            <DialogTitle>OVĚŘENÍ</DialogTitle>
                        </DialogHeader>
                        <DialogBody>
                            {authStatus === 0 ? (
                                <div>
                                    <h3 className="text-lg font-medium mb-4">
                                        Kam chcete zaslat přístupový kód?
                                    </h3>
                                    {loadingAuth && (
                                        <div className="flex justify-center mb-4">
                                            <Spinner />
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <button
                                            type="button"
                                            disabled={!authData.email}
                                            onClick={(e) => authHandler(e, "email")}
                                            className="flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed enabled:border-[#082359] enabled:hover:bg-[#082359]/5 disabled:border-gray-200"
                                        >
                                            <span className="font-semibold text-base">E-mail</span>
                                            <span className="text-sm text-gray-500 mt-1">
                                                {authData.email || "Není k dispozici"}
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            disabled={!authData.phone}
                                            onClick={(e) => authHandler(e, "sms")}
                                            className="flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed enabled:border-[#082359] enabled:hover:bg-[#082359]/5 disabled:border-gray-200"
                                        >
                                            <span className="font-semibold text-base">Telefon</span>
                                            <span className="text-sm text-gray-500 mt-1">
                                                {authData.phone || "Není k dispozici"}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={authLoginHandler}>
                                    <h3 className="text-lg font-medium mb-4">
                                        Zadejte přístupový kód
                                    </h3>
                                    <Input
                                        placeholder="Přístupový kód"
                                        value={authCode}
                                        minLength={6}
                                        maxLength={6}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete="one-time-code"
                                        onChange={(e) => setAuthCode(e.target.value)}
                                        className="mb-4 text-base"
                                    />
                                    <div className="flex items-center gap-3">
                                        <Button type="submit">Odeslat</Button>
                                        {loadingLogin && <Spinner />}
                                    </div>
                                </form>
                            )}
                        </DialogBody>
                    </DialogContent>
                </Dialog>

                <section className="min-h-screen flex flex-col items-center md:justify-center py-12 px-4">
                    <div className="w-full max-w-md mx-auto px-4">
                        <div className="flex justify-center mb-6">
                            <img src="/logo.png" alt="De Vries Group" className="h-12 object-contain" />
                        </div>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                            <h1 className="text-3xl font-semibold text-center mb-3">
                                Přihlášení
                            </h1>
                            <form onSubmit={submitHandler}>
                                <p className="text-center text-base text-gray-600 mb-6">
                                    Přihlaste se přes BankID (doporučeno), nebo zadejte Vaše
                                    rodné číslo nebo{" "}
                                    <Tooltip text="Referenční číslo je označení pohledávky, které jste obdrželi mailem, případně v dopise.">
                                        <span className="border-b border-dotted border-gray-800 cursor-help">
                                            referenční číslo
                                        </span>
                                    </Tooltip>
                                </p>

                                <a href={authUri} className="button-bankid w-full justify-center mb-2">
                                    <BankIdLogo />
                                    Přihlásit se
                                </a>

                                <div className="relative my-8">
                                    <hr className="border-gray-200" />
                                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-sm text-gray-400">
                                        nebo
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative">
                                    <div>
                                        <Label htmlFor="birthno" className="mb-1 flex items-center gap-1.5">
                                            Rodné číslo
                                            <Tooltip text="Vaše rodné číslo bez lomítka">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 cursor-help text-xs" />
                                            </Tooltip>
                                        </Label>
                                        <Input
                                            id="birthno"
                                            type="text"
                                            minLength={10}
                                            maxLength={10}
                                            value={birthNo}
                                            disabled={refNo.length > 0}
                                            onChange={(e) => setBirthNo(e.target.value)}
                                            placeholder=" "
                                        />
                                    </div>

                                    <span className="hidden sm:flex absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-2 text-xs text-gray-400 items-center gap-1 pointer-events-none z-10">
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                        nebo
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </span>

                                    <div>
                                        <Label htmlFor="refno" className="mb-1 block">
                                            Referenční číslo
                                        </Label>
                                        <Input
                                            id="refno"
                                            type="text"
                                            minLength={2}
                                            maxLength={30}
                                            value={refNo}
                                            disabled={birthNo.length > 0}
                                            onChange={(e) => setRefNo(e.target.value)}
                                            placeholder=" "
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 mt-6">
                                    <Button type="submit" className="w-full">
                                        Přihlásit
                                    </Button>
                                    {loading && <Spinner />}
                                </div>
                            </form>
                        </div>
                    </div>
                </section>
                <LegalInfo variant="footer" />
            </main>
        </>
    );
}
